<template>
  <div :class="{ 'mb-3': !isLastCourse }" :style="cardStyles">
    <!-- Top Row: Badge/Credits + Prefix:Name | Buttons -->
    <div class="d-flex flex-column flex-md-row align-start align-md-center justify-space-between">
      <!-- Course Info -->
      <div class="flex-grow-1 w-100 w-md-auto" style="min-width: 0">
        <!-- Badge and Credits -->
        <div class="d-flex align-center mb-1" style="gap: 12px">
          <span v-if="course.badge?.show" class="font-weight-bold text-uppercase" :style="badgeStyles">
            {{ course.badge.text }}
          </span>
          <span class="text-truncate" style="font-size: 12px; color: #484848; white-space: nowrap; min-width: 0">
            {{ course.credits || 3 }} credits<template v-if="course._designationName && !course._isDesignation">
              &nbsp;&bull; {{ course._designationName }}</template
            >
          </span>
        </div>

        <!-- Course Prefix: Name -->
        <div
          class="font-weight-bold"
          style="font-size: 16px; line-height: 24px; letter-spacing: -0.48px; color: #191919"
        >
          {{ course.coursePrefixDisplayLabel || course.coursePrefix }}: {{ cleanCourseName }}
        </div>
      </div>

      <!-- Action Buttons (reuses central CourseCardButtons in compact layout) -->
      <CourseCardButtons
        layout="compact"
        :course="course"
        :show-details-button="effectiveShowDetailsButton"
        :display-info="detailsOpen"
        :is-selected="isSelected"
        :is-button-disabled="course.ui?.buttonDisabled"
        :button-text="course.ui?.buttonLabel"
        :schedule-dropdown-open="dropdownOpenLocal"
        @view-details="handleViewDetails"
        @remove-course="handleRemoveCourse"
        @open-link="handleOpenLink"
        @offering-selection="handleOfferingSelection"
        @button-action="handleButtonAction"
        @update:schedule-dropdown-open="dropdownOpenLocal = $event"
      />
    </div>

    <!-- Transfer credit message (conditional) -->
    <div v-if="course.flags?.isNonEnrollable" class="mt-2">
      <p class="text-body-2 mb-0" style="color: #747474">
        You have transfer credit from another institution. View the
        <a
          href="https://transferguide.apps.asu.edu/app/home"
          target="_blank"
          style="color: #8c1d40; text-decoration: underline; font-weight: 600"
          >Transfer Credit Guide</a
        >
        for details.
      </p>
    </div>

    <!-- Course Details Expansion Panel -->
    <CourseDetailsPanel :course="course" :is-open="detailsOpen" @close="detailsOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useDisplay } from 'vuetify';
import CourseCardButtons from './CourseCardButtons.vue';
import CourseDetailsPanel from './CourseDetailsPanel.vue';
import { useRouter } from 'vue-router';
import { useCheckoutStore } from '@/stores/checkout';
import { useUserStore } from '@/stores/user';
import { useCourseActions } from '@/composables/courses/useCourseActions';

const { smAndUp } = useDisplay();

const props = defineProps({
  course: {
    type: Object,
    required: true
  },
  isLastCourse: {
    type: Boolean,
    default: false
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  showDetailsButton: {
    type: Boolean,
    default: true
  },
  dropdownOpen: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'schedule',
    validator: (value) => ['schedule', 'history', 'past'].includes(value)
  }
});

const emit = defineEmits([
  'view-details',
  'add-course',
  'remove-course',
  'dropdown-toggle',
  'button-action',
  'reload',
  'reloadDashboardOrPathway'
]);

const { navigateToCheckout, openCourseUrl, buildActionPayload } = useCourseActions();

const detailsOpen = ref(false);
const dropdownOpenLocal = ref(false);

// Sync parent-controlled prop → local state (for closing when another card opens)
watch(
  () => props.dropdownOpen,
  (val) => {
    if (val !== dropdownOpenLocal.value) {
      dropdownOpenLocal.value = val;
    }
  }
);

// Notify parent when dropdown opens/closes
watch(dropdownOpenLocal, (val) => {
  emit('dropdown-toggle', val);
});

// Hide "View details" for notAvailable cards — no URL to open and no details to show
const effectiveShowDetailsButton = computed(() => {
  if (props.course.notAvailable) return false;
  return props.showDetailsButton;
});

const cardStyles = computed(() => {
  const pad = smAndUp.value ? '24px' : '16px';
  const radius = smAndUp.value ? '24px' : '16px';
  return `background-color: #ffffff; border: 1px solid #bfbfbf; border-radius: ${radius}; padding: ${pad};`;
});

const badgeStyles = computed(() => {
  const bg = props.course.badge?.color || '#e74973';
  const textColor = props.course.badge?.textColor || '#ffffff';
  return `background-color: ${bg}; color: ${textColor} !important; font-size: 12px; line-height: 18px; font-weight: 700; letter-spacing: 0.5px; padding: 2px 16px; border-radius: 4px; white-space: nowrap;`;
});

const cleanCourseName = computed(() => {
  return (props.course.courseName || '').replace(/\s*\([^)]*\)\s*/g, '').trim();
});

/**
 * View details — opens course catalog page in new tab.
 * The courseUrl field (ea_course_url in DB) can be a catalog page or a PDF brief.
 * Only open URLs that point to actual course pages (ea.asu.edu/courses/...).
 * Filters out asset URLs (assets.ea.asu.edu) and PDF links.
 */
function handleViewDetails() {
  const candidates = [props.course.offering?.eaCourseUrl, props.course.offerings?.[0]?.courseUrl].filter(Boolean);

  // Pick the first URL that looks like a catalog page, not a PDF/asset
  const url = candidates.find((u) => !u.includes('assets.ea.asu.edu') && !u.endsWith('.pdf')) || '';
  if (url) {
    // Ensure URL uses courses.ea.asu.edu subdomain (same as EnrolledPathwayCard)
    const formattedUrl = url.includes('courses.ea.asu.edu') ? url : url.replace('ea.asu.edu', 'courses.ea.asu.edu');
    window.open(formattedUrl, '_blank', 'noopener,noreferrer');
  }
  emit('view-details', props.course);
}

function handleRemoveCourse() {
  emit('remove-course', {
    courseId: props.course.courseId,
    coursePrefix: props.course.coursePrefix,
    courseOfferingId: props.course.offering?.courseOfferingId,
    courseStartDate: props.course.offering?.startDate
  });
}

function handleOfferingSelection(option) {
  emit('add-course', {
    courseId: props.course.courseId,
    coursePrefix: props.course.coursePrefix,
    courseOfferingId: option.offering.offeringId,
    courseStartDate: option.offering.startDate,
    course: props.course
  });
}

function handleOpenLink() {
  const linkUrl = props.course.section?.overrides?.ui?.linkUrl;
  if (linkUrl) {
    window.open(linkUrl, '_blank', 'noopener,noreferrer');
  }
}

const checkoutStore = useCheckoutStore();
const router = useRouter();

function handleButtonAction() {
  const actionType = props.course.ui?.actionType;

  // V2 API provides actionType — handle known actions directly
  if (actionType) {
    switch (actionType) {
      case 'checkout': {
        const checkoutData = props.course.ui?.checkoutData;
        if (checkoutData) {
          checkoutStore.setCheckoutData(checkoutData);
          router.push({ name: 'courseCheckout' });
          return;
        }
        break;
      }
      case 'conclude':
      case 'open-course':
      case 'link': {
        const url = props.course.ui?.linkUrl;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      case 'verify-identity':
        router.push({ name: 'id-verification' });
        return;
      case 'retry-credit': {
        const userStore = useUserStore();
        const asurite = userStore.apis?.learnerData?._asuRite;
        const enrollmentId = props.course.ui?.enrollmentId;
        router.push({ path: '/add-to-transcript', query: { asurite, enrollmentId } });
        return;
      }
    }
  }

  // Fallback: emit for parent to handle (legacy flow without actionType)
  emit('button-action', buildActionPayload(props.course));
}
</script>
